/**
 * FormTextField - Text input field integrated with React Hook Form
 *
 * Replaces: TextFieldValidator from react-form-validator-core
 */

import { useId } from "react";
import { useController, useFormContext } from "react-hook-form";
import type { InputType } from "@skypointcloud/skypoint-ui";
import { FormField } from "./FormField";
import { SkyInputNative } from "./SkyInputNative";

export interface FormTextFieldProps {
  /** Field name (required, should match form schema key) */
  name: string;

  /** Field label */
  label?: string;

  /** Whether field is required */
  required?: boolean;

  /** Optional hint text to display below the field */
  hint?: string;

  /** Custom validation state (overrides form context) */
  validationState?: "error" | "warning" | "success" | "none";

  /** Custom validation message (overrides form context) */
  validationMessage?: string;

  /** Default value for uncontrolled mode */
  defaultValue?: string;

  /** Transform value before onChange */
  transformValue?: (value: string) => string;

  /** Additional CSS class name */
  className?: string;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Input type */
  type?: InputType;

  /** Placeholder text */
  placeholder?: string;

  /** Element displayed after input text */
  contentAfter?: React.ReactElement;

  /** Element displayed before input text */
  contentBefore?: React.ReactElement;
}

/**
 * FormTextField Component
 *
 * A text input field that integrates with React Hook Form.
 * Provides automatic validation, error handling, and skypoint-ui styling.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <FormTextField
 *   name="username"
 *   label="Username"
 *   required
 *   placeholder="Enter username"
 * />
 *
 * // With transformation
 * <FormTextField
 *   name="email"
 *   label="Email"
 *   type="email"
 *   transformValue={(v) => v.toLowerCase().trim()}
 * />
 *
 * // With hint
 * <FormTextField
 *   name="password"
 *   label="Password"
 *   type="password"
 *   hint="Must be at least 8 characters"
 * />
 * ```
 */
export const FormTextField: React.FC<FormTextFieldProps> = ({
  name,
  label,
  required,
  hint,
  validationState,
  validationMessage,
  defaultValue,
  transformValue,
  className,
  disabled,
  type = "text",
  placeholder,
  contentAfter,
  contentBefore,
}) => {
  const { control } = useFormContext();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: defaultValue || (type === "number" ? 0 : ""),
  });

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    let value: string | number = e.target.value;

    // Convert to number for number inputs
    if (type === "number") {
      const numValue = parseFloat(e.target.value);
      value = isNaN(numValue) ? 0 : numValue;
    }

    // Apply transformation if provided (for string types)
    if (transformValue && typeof value === "string") {
      value = transformValue(value);
    }

    field.onChange(value);
  };

  // SkyInput drops every prop outside SkyInputProps, aria-* included, so the
  // aria wiring below only reaches the DOM through SkyInputNative. It also has
  // to point at ids that exist: FormField renders the error and hint, so it
  // owns those ids and is given the same fieldId to build them from.
  const fieldId = useId();

  // Mirror FormField's own resolution exactly. An error can arrive from React
  // Hook Form OR from the validationState/validationMessage props, and FormField
  // renders the error span for either -- so deriving aria-invalid from the RHF
  // error alone leaves a visible error the input never announces.
  const errorText = validationMessage ?? (error?.message as string | undefined);
  const isInvalid =
    (validationState ?? (error ? "error" : "none")) === "error" && Boolean(errorText);
  const describedBy =
    [isInvalid ? `${fieldId}-error` : null, hint ? `${fieldId}-hint` : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <FormField
      name={name}
      label={label}
      required={required}
      hint={hint}
      validationState={validationState}
      validationMessage={validationMessage}
      className={className}
      fieldId={fieldId}
    >
      <SkyInputNative
        id={fieldId}
        type={type}
        value={field.value || ""}
        onChange={handleChange}
        onBlur={field.onBlur}
        disabled={disabled}
        placeholder={placeholder}
        contentAfter={contentAfter}
        contentBefore={contentBefore}
        aria-invalid={isInvalid ? true : undefined}
        aria-describedby={describedBy}
      />
    </FormField>
  );
};
